import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { FaCheck, FaClock } from 'react-icons/fa';
import { useSnackbar } from 'v3/components/Snackbar';
import { Modal, Text, Button } from 'v3/components/';
import theme from 'v3/theme';
import apiReport from 'services/apis/report';

import Loading from 'components/Loading';
import { formatDateCustom, formatTime } from 'utils/formatter';
import moment from 'moment';
import * as Styled from './styles';

function DateTimeContent({ value }) {
  if (!value) {
    return <Text type="body" color="dark">Não informado</Text>;
  }

  return (
    <>
      <Text type="body" color="dark" className="d-block">
        {formatDateCustom(value, 'DD, MMMM [de] YYYY')}
      </Text>
      <Styled.TimeInfo>
        <FaClock color={theme.colors.dark} />
        <Text type="label" color="dark">
          {formatTime(value)}
        </Text>
      </Styled.TimeInfo>
    </>
  );
}

export function Destination({ destination }) {
  const type = useMemo(() => {
    if (!destination?.action) {
      return 'Origem';
    }

    if (destination.action === 'delivery') {
      return 'Entrega';
    }

    return 'Aduana';
  }, [destination]);

  const waitingTime = useMemo(() => {
    if (!destination?.effective_time || !destination?.arrival_time) {
      return '';
    }

    const diff = moment(destination.effective_time).diff(
      moment(destination.arrival_time),
      'minutes',
    );

    if (diff < 0) {
      return '';
    }

    const hours = parseInt(diff / 60, 10);
    const minutes = diff % 60;

    if (typeof diff === 'number') {
      if (hours < 10) {
        if (minutes < 10) {
          return `0${hours}:0${minutes}`;
        }

        return `0${hours}:${minutes}`;
      }

      if (minutes < 10) {
        return `${hours}:0${minutes}`;
      }

      return `${hours}:${minutes}`;
    }

    return '';
  }, [destination?.arrival_time, destination?.effective_time]);

  const status = useMemo(() => {
    if (destination?.arrival_time && destination?.effective_time) {
      return 'completed';
    }
    if (destination?.arrival_time) {
      return 'progress';
    }

    return 'pending';
  }, [destination?.arrival_time, destination?.effective_time]);

  return (
    <Styled.DestinationWrap
      className="position-relative"
      completed={status === 'completed'}
      progress={status === 'progress'}
    >
      <Styled.InfoColumn xs="4">
        <Styled.TimeLine>
          <Styled.Check>
            <FaCheck color="#fff" size={9} className="position-relative" />
          </Styled.Check>
        </Styled.TimeLine>
        <Styled.InfoContent>
          <Text type="body" color="dark" weight={700} className="d-block">
            {type}
          </Text>
          <Text type="body" color="dark" className="d-block">
            {destination?.formatted_address || 'Não informado'}
          </Text>
        </Styled.InfoContent>
      </Styled.InfoColumn>
      <Styled.TimeColumns xs="8">
        <Styled.DestinationTimes className="flex-nowrap no-gutters">
          <Styled.TimeCell className="text-center">
            <DateTimeContent value={destination?.start_schedule} />
          </Styled.TimeCell>
          <Styled.TimeCell className="text-center">
            <DateTimeContent value={destination?.arrival_time} />
          </Styled.TimeCell>
          <Styled.WaitingCell xs="1" className="text-center">
            {waitingTime ? (
              <Styled.TimeInfo className="justify-content-center">
                <FaClock color={theme.colors.dark} />
                <Text type="label" color="dark">
                  {waitingTime}
                </Text>
              </Styled.TimeInfo>
            ) : null}
          </Styled.WaitingCell>
          <Styled.TimeCell className="text-center">
            <DateTimeContent value={destination?.effective_time} />
          </Styled.TimeCell>
        </Styled.DestinationTimes>
      </Styled.TimeColumns>
    </Styled.DestinationWrap>
  );
}

export function DestinationsModal({ isOpen, onClose, destination, travelId }) {
  const snackbar = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [origin, setOrigin] = useState(null);
  const [destinations, setDestinations] = useState([]);

  async function getLoadDestionations(id) {
    setLoading(true);
    try {
      const { data } = await apiReport.get(`travels/${id}/destinations`);
      const [originData, ...destinationsData] = data;
      setOrigin(originData);
      setDestinations(destinationsData);
    } catch (err) {
      if (err.data?.response?.message) {
        snackbar.show(<Text>{err.data.response.message}</Text>, {
          type: 'error',
        });
      }

      snackbar.show(<Text>Erro ao buscar as origens e destinos</Text>, {
        type: 'error',
      });
    }

    setLoading(false);
  }

  useEffect(() => {
    if (isOpen && destination?.loadId) {
      getLoadDestionations(destination?.loadId);
    }
  }, [destination?.loadId, isOpen]);

  return (
    <Modal
      show={isOpen}
      handleClose={onClose}
      heading={
        <Text
          type="header"
          color="dark"
          weight={700}
          className="d-flex align-items-center"
          style={{ gap: 8 }}
        >
          Origem e Destinos
          <Styled.TravelLink
            to={`/viagens/${destination?.travelId || travelId}`}
            target="_blank"
          >
            Viagem {destination?.travelId || travelId}
            {destination?.externalId ? ` - ${destination?.externalId}` : ''}
          </Styled.TravelLink>
        </Text>
      }
      body={
        <Styled.ModalContent>
          {loading ? (
            <Styled.LoadingWrapper className="text-center">
              <Loading />
            </Styled.LoadingWrapper>
          ) : (
            <>
              <Styled.SectionHeader className="align-items-center">
                <Col xs="4">
                  <Text type="body" color="dark" weight={700} className="d-block">
                    Origem
                  </Text>
                </Col>
                <Col xs="8">
                  <Styled.HeaderColumns className="flex-nowrap no-gutters">
                    <Styled.HeaderCell className="text-center">
                      <Text type="body" color="dark" weight={700} className="d-block">
                        Agendado
                      </Text>
                    </Styled.HeaderCell>
                    <Styled.HeaderCell className="text-center">
                      <Text type="body" color="dark" weight={700} className="d-block">
                        Chegada
                      </Text>
                    </Styled.HeaderCell>
                    <Styled.WaitingHeader xs="1" className="text-center">
                      &nbsp;
                    </Styled.WaitingHeader>
                    <Styled.HeaderCell className="text-center">
                      <Text type="body" color="dark" weight={700} className="d-block">
                        Efetivado
                      </Text>
                    </Styled.HeaderCell>
                  </Styled.HeaderColumns>
                </Col>
              </Styled.SectionHeader>
              {origin && <Destination destination={origin} />}
              <Styled.SectionDivider />
              <Styled.SectionTitle>
                <Text type="body" color="dark" weight={700} className="d-block">
                  Destino
                </Text>
              </Styled.SectionTitle>
              {destinations.map((destination) => (
                <Destination key={destination.id} destination={destination} />
              ))}
            </>
          )}
        </Styled.ModalContent>
      }
      footer={
        <Button onClick={onClose} variant="primary">
          Fechar
        </Button>
      }
    />
  );
}
